import { useTranslation } from "react-i18next";

import schedaLogo from "@/assets/brand/scheda-logo-horizontal.svg";
import adPeople from "@/assets/marketing/ad-people.jpg";
import adTrades from "@/assets/marketing/ad-trades.jpg";
import plannerBoard from "@/assets/marketing/planner-board.png";
import stackBoards from "@/assets/marketing/stack-boards.png";

/**
 * The Scheda advert on the Delgentic apex, in the slot the WebGL agent mesh used to fill.
 *
 * <p>The apex is the company page and Scheda is the product, so this is deliberately shaped
 * as an <b>advert</b> rather than a section: one silhouette, its own brand mark, its own way
 * in. It is also the piece that finally answers the follow-up left open by #170, which was to
 * give the apex an app-style CTA instead of auth controls that this origin cannot honour.
 *
 * <p>Two pairs of people cross-fade on a 9.5s cycle (4.75s a frame), each with its own
 * rendered object floating between them. Both photographs were generated with identical
 * framing and lighting for exactly this reason: the object lands in the same place either
 * way, so only the object and the people change, never the composition. If you replace one
 * frame, replace both or the pair stops matching. Provenance for every asset is in
 * {@code docs/marketing/assets/README.md}.
 *
 * <p>⚠️ THE CLASS NAMES ARE `promo-*`, NOT `ad-*`, AND MUST STAY THAT WAY. This shipped as
 * `.ad`, `.ad-slot`, `.ad-bar` and friends, which are canonical ad-network class names carried
 * in EasyList. Blockers filter them cosmetically, so on a machine with one installed the object
 * between the two figures simply was not there while the rest of the unit rendered — a bug that
 * is invisible to every check that does not run a blocker. `marketing.test.tsx` guards it.
 *
 * <p>THE INVARIANT: this component is reached from the apex bundle, which has no
 * {@code SessionProvider} and no OIDC provider. It must never call {@code useSession()} or
 * {@code useAuth()} — an image, an SVG and a callback are safe by construction. See
 * {@code catalog/CatalogApp.tsx}.
 */
export function SchedaAd({ onTry }: { onTry: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="promo">
      <span className="promo-eyebrow">{t("ad.eyebrow")}</span>

      <div className="promo-shot">
        <div className="car">
          <img src={adPeople} alt={t("ad.altDesk")} />
          {/* Frame two is the one carrying the argument: a blazer and a tool belt do not
              share a calendar system, which is the harder booking and the more honest
              picture of what cross-organisation scheduling is for. */}
          <img className="b" src={adTrades} alt={t("ad.altSite")} loading="lazy" />
        </div>
        <div className="car-dots" aria-hidden="true">
          <i />
          <i />
        </div>

        {/* One object per frame, fading on the same clock as the photograph behind it. */}
        <div className="promo-object promo-object--a">
          <div className="obj">
            <img src={stackBoards} alt={t("ad.altStack")} />
            {/* Tiles drift in and are absorbed; the fifth lifts back out and relocates.
                Decorative on top of a fixed render, hence aria-hidden. */}
            <svg viewBox="0 0 200 163" aria-hidden="true">
              <g className="fl1">
                <rect x="40" y="46" width="17" height="17" rx="5" fill="#6388d1" />
                <rect x="40" y="59" width="17" height="4" rx="2" fill="#4f6fb4" />
              </g>
              <g className="fl2">
                <rect x="126" y="40" width="17" height="17" rx="5" fill="#73cdba" />
                <rect x="126" y="53" width="17" height="4" rx="2" fill="#5bb2a0" />
              </g>
              <g className="fl3">
                <rect x="38" y="100" width="17" height="17" rx="5" fill="#f78d6f" />
                <rect x="38" y="113" width="17" height="4" rx="2" fill="#d9755a" />
              </g>
              <g className="fl4">
                <rect x="130" y="96" width="17" height="17" rx="5" fill="#f1cd7a" />
                <rect x="130" y="109" width="17" height="4" rx="2" fill="#d4b163" />
              </g>
              <g className="fl5">
                <rect x="84" y="30" width="18" height="18" rx="5" fill="#73cdba" />
                <rect x="84" y="44" width="18" height="4" rx="2" fill="#5bb2a0" />
              </g>
            </svg>
          </div>
        </div>

        <div className="promo-object promo-object--b">
          <div className="obj">
            <img src={plannerBoard} alt={t("ad.altPlanner")} loading="lazy" />
            <svg viewBox="0 0 200 169" aria-hidden="true">
              <g className="fl1">
                <rect x="46" y="52" width="46" height="12" rx="6" fill="#6388d1" />
              </g>
              <g className="fl3">
                <rect x="44" y="112" width="46" height="12" rx="6" fill="#f78d6f" />
              </g>
              <g className="fl5">
                <rect x="84" y="34" width="44" height="12" rx="6" fill="#73cdba" />
              </g>
            </svg>
          </div>
        </div>
      </div>

      {/* Poster build: the message sits over the picture behind a white fade, so the unit
          keeps one silhouette. That is what makes it read as an advert rather than a band
          of the page — at the cost of the copy sitting on live artwork, which is why both
          carousel frames were checked for legibility under this exact gradient. */}
      <div className="promo-bar">
        <div>
          <img className="promo-logo" src={schedaLogo} alt="Scheda" />
          <p className="promo-line">{t("ad.line")}</p>
        </div>
        <button type="button" className="promo-cta" onClick={onTry}>
          {t("ad.cta")}
          <svg
            width="13"
            height="13"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="M3 7h8M7.5 3.5 11 7l-3.5 3.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}
